import React, { useEffect, useState } from 'react';
import { AlertCircle, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Typography,
} from '@roger/ui-components';
import {
  client,
  getBillingCycleEoePreview,
  getBillingCycleVisitsAndSupplies,
  updateBillingWorksheet,
} from '../../services/api';
import type {
  ActiveBillingCycleItem,
  BillableStatus,
  BillingCycleSupplyItem,
  BillingCycleVisitItem,
  BillingCycleVisitsAndSuppliesResponse,
  EoePreviewResponse,
} from '../../services/api/types.gen';
import { formatDateToMMDDYYYY } from '../../utils/dateUtils';
import {
  isValidDollarInput,
  parseDollarAmount,
} from '../../utils/currencyUtils';

interface EOEClaimsWorksheetProps {
  billingCycle: ActiveBillingCycleItem;
  onClose: () => void;
}

/** Unset/unknown statuses are treated as BILLABLE so rows are not dropped from all groups. */
function visitBillableGroupKey(
  status: string | null | undefined
): 'BILLABLE' | 'NON_BILLABLE' | 'NON_COVERED' {
  const s = (status ?? '').trim().toUpperCase();
  if (s === 'NON_BILLABLE') return 'NON_BILLABLE';
  if (s === 'NON_COVERED') return 'NON_COVERED';
  return 'BILLABLE';
}

function supplyBillableGroupKey(
  status: string | null | undefined
): 'BILLABLE' | 'NON_BILLABLE' {
  const s = (status ?? '').trim().toUpperCase();
  if (s === 'NON_BILLABLE' || s === 'NON_COVERED') return 'NON_BILLABLE';
  return 'BILLABLE';
}

const renderSection = (
  title: string,
  rows: Array<{ label: string; value: string }>
) => (
  <Card className='overflow-hidden p-0'>
    <CardHeader variant='highlighted'>
      <CardTitle variant='highlighted'>{title}</CardTitle>
    </CardHeader>
    <CardContent className='py-4'>
      <div className='space-y-0'>
        {rows.map((row, index) => (
          <div
            key={row.label}
            className={`grid grid-cols-[200px_1fr] items-baseline gap-6 px-6 py-2.5 ${
              index % 2 === 0 ? 'bg-white' : 'bg-slate-100'
            }`}
          >
            <Typography
              as='span'
              className='text-slate-700 font-medium text-sm'
            >
              {row.label}
            </Typography>
            <Typography as='span' className='text-slate-900 text-sm'>
              {row.value}
            </Typography>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

const EOEClaimsWorksheet: React.FC<EOEClaimsWorksheetProps> = ({
  billingCycle,
  onClose,
}) => {
  const [preview, setPreview] = useState<EoePreviewResponse | null>(null);
  const [visitsData, setVisitsData] =
    useState<BillingCycleVisitsAndSuppliesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isVisitsExpanded, setIsVisitsExpanded] = useState(true);
  const [isSuppliesExpanded, setIsSuppliesExpanded] = useState(true);
  const [pendingBillingStatusChanges, setPendingBillingStatusChanges] =
    useState<Record<string, BillableStatus>>({});
  const [pendingBulkSupplyCost, setPendingBulkSupplyCost] = useState<
    string | null
  >(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const [previewResponse, visitsResponse] = await Promise.all([
          getBillingCycleEoePreview({
            path: { billing_cycle_id: billingCycle.billing_cycle_id },
            client,
          }),
          getBillingCycleVisitsAndSupplies({
            path: { billing_cycle_id: billingCycle.billing_cycle_id },
            client,
          }),
        ]);
        setPreview(previewResponse?.data ?? null);
        setVisitsData(visitsResponse?.data ?? null);
      } catch (err) {
        console.error('Failed to load billing cycle worksheet:', err);
        setError('Unable to load billing cycle details.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAll();
  }, [billingCycle.billing_cycle_id]);

  const handleSave = async () => {
    const visitUpdates = Object.entries(pendingBillingStatusChanges).map(
      ([visit_id, new_billing_status]) => ({ visit_id, new_billing_status })
    );
    const parsedBulkCost =
      pendingBulkSupplyCost !== null
        ? parseDollarAmount(pendingBulkSupplyCost)
        : undefined;
    const hasChanges =
      visitUpdates.length > 0 || pendingBulkSupplyCost !== null;
    if (!hasChanges) {
      onClose();
      return;
    }
    try {
      setIsSaving(true);
      setSaveError(null);
      await updateBillingWorksheet({
        body: {
          billing_cycle_id: billingCycle.billing_cycle_id,
          visit_updates: visitUpdates,
          bulk_supply_cost: parsedBulkCost,
        },
        client,
      });
      setPendingBillingStatusChanges({});
      setPendingBulkSupplyCost(null);
      onClose();
    } catch (err) {
      console.error(
        err instanceof Error ? err.message : 'Failed to save changes.'
      );
      setSaveError('Failed to save changes.');
    } finally {
      setIsSaving(false);
    }
  };

  const displayValue = (value?: string | null): string =>
    value && value.trim() !== '' ? value : '-';

  const displayDate = (value?: string | null): string =>
    formatDateToMMDDYYYY(value) || '-';

  const getMiddleInitial = (middleName?: string | null): string =>
    middleName?.trim()?.charAt(0)?.toUpperCase() || '-';

  const demographics = preview?.patient_demographics;
  const demographicsRows = demographics
    ? [
        { label: 'Last name', value: displayValue(demographics.last_name) },
        { label: 'First name', value: displayValue(demographics.first_name) },
        {
          label: 'Middle initial',
          value: getMiddleInitial(demographics.middle_name),
        },
        { label: 'Gender', value: displayValue(demographics.gender) },
        {
          label: 'Date of birth',
          value: displayDate(demographics.date_of_birth),
        },
        {
          label: 'Medicare number',
          value: displayValue(demographics.medicare_number),
        },
        {
          label: 'Medical record number',
          value: displayValue(demographics.mrn),
        },
        { label: 'Phone number', value: displayValue(demographics.phone) },
      ]
    : [];

  const address = preview?.patient_address;
  const addressRows = address
    ? [
        { label: 'Address', value: displayValue(address.address) },
        { label: 'City', value: displayValue(address.city) },
        { label: 'State', value: displayValue(address.state) },
        { label: 'Zip', value: displayValue(address.zip_code) },
      ]
    : [];

  const physician = preview?.physician_information;
  const physicianRows = physician
    ? [
        { label: 'Last name', value: displayValue(physician.last_name) },
        { label: 'First name', value: displayValue(physician.first_name) },
        { label: 'Physician NPI', value: displayValue(physician.npi) },
        { label: 'PECOS status', value: displayValue(physician.pecos_status) },
      ]
    : [];

  const episodeInfo = preview?.episode_information;
  const episodeRows = [
    { label: 'Type of bill', value: displayValue(episodeInfo?.type_of_bill) },
    { label: 'Start of care date', value: displayDate(episodeInfo?.soc_date) },
    {
      label: 'Assessment date',
      value: displayDate(episodeInfo?.assessment_date),
    },
    {
      label: 'Episode start date',
      value: displayDate(episodeInfo?.episode_start_date),
    },
    {
      label: 'First billable visit',
      value: displayDate(episodeInfo?.first_billable_visit),
    },
  ];

  const admissionInfo = preview?.admission_information;
  const admissionRows = [
    {
      label: 'Point of origin',
      value: displayValue(admissionInfo?.point_of_origin),
    },
    {
      label: 'Admission type',
      value: displayValue(admissionInfo?.admission_type),
    },
    {
      label: 'Condition code',
      value: displayValue(admissionInfo?.condition_code),
    },
    {
      label: 'Admission diagnosis code',
      value: displayValue(admissionInfo?.diagnosis_code),
    },
    { label: 'Value code', value: displayValue(admissionInfo?.value_code) },
    {
      label: 'Value code amount',
      value: displayValue(admissionInfo?.value_code_amount),
    },
  ];

  const renderOasisIcd10Card = (): React.ReactNode => {
    if (!preview) {
      return null;
    }
    const section = preview.oasis_diagnoses_from_visit;
    return (
      <Card className='overflow-hidden p-0 w-full'>
        <CardHeader variant='highlighted'>
          <CardTitle variant='highlighted'>Diagnosis Information</CardTitle>
        </CardHeader>
        <CardContent className='py-4'>
          {!section ? (
            <Typography
              variant='small'
              as='p'
              className='px-6 text-muted-foreground'
            >
              No diagnosis codes on OASIS visits linked to this billing cycle.
            </Typography>
          ) : (
            <>
              <Typography
                variant='small'
                as='p'
                className='px-6 pb-3 text-slate-600'
              >
                Latest visit with diagnoses: {section.source_visit_task}
                {section.source_visit_date
                  ? ` · ${displayDate(section.source_visit_date)}`
                  : null}
              </Typography>
              <table className='w-full text-sm'>
                <thead>
                  <tr className='border-b border-slate-200 bg-slate-100'>
                    <th className='text-left px-6 py-2 text-xs font-semibold text-slate-600 uppercase tracking-wide'>
                      Role
                    </th>
                    <th className='text-left px-6 py-2 text-xs font-semibold text-slate-600 uppercase tracking-wide'>
                      Code
                    </th>
                    <th className='text-left px-6 py-2 text-xs font-semibold text-slate-600 uppercase tracking-wide'>
                      Description
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {section.diagnoses.map((dx, index) => (
                    <tr
                      key={`${dx.code}-${dx.position}`}
                      className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}
                    >
                      <td className='px-6 py-2.5 text-slate-900 whitespace-nowrap'>
                        {dx.is_primary ? 'Primary' : 'Secondary'}
                      </td>
                      <td className='px-6 py-2.5 font-mono text-slate-900 whitespace-nowrap'>
                        {dx.code}
                      </td>
                      <td className='px-6 py-2.5 text-slate-900'>
                        {displayValue(dx.description)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className='fixed inset-0 z-50 bg-background flex flex-col'>
      <div className='flex items-center justify-between px-6 py-4 border-b'>
        <h1 className='text-xl font-semibold'>Claims Worksheet</h1>
        <span className='text-muted-foreground text-sm'>
          {billingCycle.patient_name} — {billingCycle.patient_mrn}
        </span>
      </div>

      <div className='flex-1 overflow-auto p-6'>
        {isLoading ? (
          <div className='flex flex-col items-center justify-center py-20 gap-2 text-muted-foreground'>
            <Loader2 className='h-6 w-6 animate-spin' />
            <Typography variant='small' as='p'>
              Loading billing cycle details...
            </Typography>
          </div>
        ) : error ? (
          <Alert className='border-red-200 bg-red-50'>
            <AlertCircle className='h-4 w-4 text-red-600' />
            <AlertDescription className='text-red-800'>
              <p>
                An error happened while loading billing cycle details. Please
                try again and contact support if the issue persists.
              </p>
              <p className='mt-2'>{error}</p>
            </AlertDescription>
          </Alert>
        ) : preview ? (
          <div className='flex flex-col gap-4'>
            <div className='grid grid-cols-1 xl:grid-cols-2 gap-4'>
              {renderSection('Patient Demographics', demographicsRows)}
              {renderSection('Patient Address', addressRows)}
              {renderSection('Physician Information', physicianRows)}
              {renderSection('Episode Information', episodeRows)}
              {renderSection('Admission Information', admissionRows)}
            </div>
            {renderOasisIcd10Card()}
            <Card className='overflow-hidden p-0 w-full'>
              <CardHeader
                variant='highlighted'
                className='cursor-pointer select-none'
                onClick={() => setIsVisitsExpanded(!isVisitsExpanded)}
              >
                <div className='flex items-center justify-between'>
                  <CardTitle variant='highlighted'>Visits</CardTitle>
                  {isVisitsExpanded ? (
                    <ChevronUp className='h-4 w-4 text-white' />
                  ) : (
                    <ChevronDown className='h-4 w-4 text-white' />
                  )}
                </div>
              </CardHeader>
              {isVisitsExpanded && (
                <CardContent className='p-0'>
                  {(() => {
                    const VISIT_GROUPS: Array<{
                      label: string;
                      key: string;
                    }> = [
                      { label: 'Billable Visits', key: 'BILLABLE' },
                      { label: 'Non-Billable Visits', key: 'NON_BILLABLE' },
                      { label: 'Non-Covered Visits', key: 'NON_COVERED' },
                    ];

                    const COLUMNS = [
                      'Discipline',
                      'Visit Date',
                      'G Code',
                      'Q Code',
                      'Status',
                      'Units',
                      'Charge',
                      'Billable Status',
                    ];

                    const allVisits = visitsData?.visits ?? [];

                    return VISIT_GROUPS.map(({ label, key }, groupIndex) => {
                      const groupVisits = allVisits.filter(
                        v => visitBillableGroupKey(v.billable_status) === key
                      );
                      return (
                        <div
                          key={key}
                          className={
                            groupIndex < VISIT_GROUPS.length - 1
                              ? 'border-b'
                              : ''
                          }
                        >
                          <div className='px-4 py-2 bg-slate-50 border-b'>
                            <Typography
                              as='span'
                              className='text-xs font-semibold text-slate-500 uppercase tracking-wide'
                            >
                              {label}
                            </Typography>
                          </div>
                          {groupVisits.length > 0 ? (
                            <table className='w-full text-sm ml-4'>
                              <thead>
                                <tr className='bg-slate-100 border-b'>
                                  {COLUMNS.map(header => (
                                    <th
                                      key={header}
                                      className='text-left px-4 py-2 text-xs font-semibold text-slate-600 uppercase tracking-wide whitespace-nowrap'
                                    >
                                      {header}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {groupVisits.map(
                                  (
                                    visit: BillingCycleVisitItem,
                                    index: number
                                  ) => (
                                    <tr
                                      key={index}
                                      className={
                                        index % 2 === 0
                                          ? 'bg-white'
                                          : 'bg-slate-50'
                                      }
                                    >
                                      <td className='px-4 py-2.5 text-slate-900'>
                                        {visit.discipline ?? '-'}
                                      </td>
                                      <td className='px-4 py-2.5 text-slate-900 whitespace-nowrap'>
                                        {displayDate(visit.visit_date)}
                                      </td>
                                      <td className='px-4 py-2.5 text-slate-900'>
                                        {visit.hcpcs_g_code}
                                      </td>
                                      <td className='px-4 py-2.5 text-slate-900'>
                                        {visit.hcpcs_q_code}
                                      </td>
                                      <td className='px-4 py-2.5 text-slate-900'>
                                        {visit.visit_status}
                                      </td>
                                      <td className='px-4 py-2.5 text-slate-900'>
                                        {visit.units}
                                      </td>
                                      <td className='px-4 py-2.5 text-slate-900'>
                                        {visit.charge}
                                      </td>
                                      <td className='px-4 py-2'>
                                        <Select
                                          value={
                                            pendingBillingStatusChanges[
                                              visit.visit_id
                                            ] ??
                                            visitBillableGroupKey(
                                              visit.billable_status
                                            )
                                          }
                                          onValueChange={value =>
                                            setPendingBillingStatusChanges(
                                              prev => ({
                                                ...prev,
                                                [visit.visit_id]:
                                                  value as BillableStatus,
                                              })
                                            )
                                          }
                                        >
                                          <SelectTrigger
                                            selectSize='sm'
                                            className='w-36'
                                          >
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value='BILLABLE'>
                                              Billable
                                            </SelectItem>
                                            <SelectItem value='NON_BILLABLE'>
                                              Non-Billable
                                            </SelectItem>
                                            <SelectItem value='NON_COVERED'>
                                              Non-Covered
                                            </SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </td>
                                    </tr>
                                  )
                                )}
                              </tbody>
                            </table>
                          ) : (
                            <div className='px-6 py-3'>
                              <Typography
                                variant='small'
                                as='p'
                                className='text-muted-foreground italic'
                              >
                                No {label.toLowerCase()} for this billing cycle.
                              </Typography>
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </CardContent>
              )}
            </Card>
            <Card className='overflow-hidden p-0 w-full'>
              <CardHeader
                variant='highlighted'
                className='cursor-pointer select-none'
                onClick={() => setIsSuppliesExpanded(!isSuppliesExpanded)}
              >
                <div className='flex items-center justify-between'>
                  <CardTitle variant='highlighted'>Supplies</CardTitle>
                  {isSuppliesExpanded ? (
                    <ChevronUp className='h-4 w-4 text-white' />
                  ) : (
                    <ChevronDown className='h-4 w-4 text-white' />
                  )}
                </div>
              </CardHeader>
              {isSuppliesExpanded && (
                <CardContent className='p-0'>
                  {(() => {
                    const SUPPLY_GROUPS: Array<{
                      label: string;
                      key: string;
                    }> = [
                      { label: 'Billable Supplies', key: 'BILLABLE' },
                      { label: 'Non-Billable Supplies', key: 'NON_BILLABLE' },
                    ];

                    const COLUMNS = [
                      'Date',
                      'Supply',
                      'Quantity',
                      'Unit Cost',
                      'Total',
                      'Billable Status',
                    ];

                    const allSupplies = visitsData?.supplies ?? [];

                    return SUPPLY_GROUPS.map(({ label, key }, groupIndex) => {
                      const groupSupplies = allSupplies.filter(
                        s => supplyBillableGroupKey(s.billable_status) === key
                      );
                      return (
                        <div
                          key={key}
                          className={
                            groupIndex < SUPPLY_GROUPS.length - 1
                              ? 'border-b'
                              : ''
                          }
                        >
                          <div className='px-4 py-2 bg-slate-50 border-b'>
                            <Typography
                              as='span'
                              className='text-xs font-semibold text-slate-500 uppercase tracking-wide'
                            >
                              {label}
                            </Typography>
                          </div>
                          {groupSupplies.length > 0 ? (
                            <table className='w-full text-sm ml-4'>
                              <thead>
                                <tr className='bg-slate-100 border-b'>
                                  {COLUMNS.map(header => (
                                    <th
                                      key={header}
                                      className='text-left px-4 py-2 text-xs font-semibold text-slate-600 uppercase tracking-wide whitespace-nowrap'
                                    >
                                      {header}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {groupSupplies.map(
                                  (
                                    supply: BillingCycleSupplyItem,
                                    index: number
                                  ) => (
                                    <tr
                                      key={index}
                                      className={
                                        index % 2 === 0
                                          ? 'bg-white'
                                          : 'bg-slate-50'
                                      }
                                    >
                                      <td className='px-4 py-2.5 text-slate-900 whitespace-nowrap'>
                                        {displayDate(supply.supply_date)}
                                      </td>
                                      <td className='px-4 py-2.5 text-slate-900'>
                                        {supply.supply ?? '-'}
                                      </td>
                                      <td className='px-4 py-2.5 text-slate-900'>
                                        {supply.quantity ?? '-'}
                                      </td>
                                      <td className='px-4 py-2.5 text-slate-900'>
                                        {supply.unit_cost ?? '-'}
                                      </td>
                                      <td className='px-4 py-2.5 text-slate-900'>
                                        {supply.total ?? '-'}
                                      </td>
                                      <td className='px-4 py-2.5 text-slate-900'>
                                        {supply.billable_status ?? '-'}
                                      </td>
                                    </tr>
                                  )
                                )}
                              </tbody>
                            </table>
                          ) : (
                            <div className='px-6 py-3'>
                              <Typography
                                variant='small'
                                as='p'
                                className='text-muted-foreground italic'
                              >
                                No {label.toLowerCase()} for this billing cycle.
                              </Typography>
                            </div>
                          )}
                          {key === 'BILLABLE' && (
                            <div className='flex items-center gap-3 px-6 py-3 border-t bg-slate-50'>
                              <Typography
                                as='span'
                                className='text-sm font-medium text-slate-700 whitespace-nowrap'
                              >
                                Bulk Supply Charge ($)
                              </Typography>
                              <Input
                                inputSize='sm'
                                type='text'
                                inputMode='decimal'
                                placeholder='0.00'
                                className='w-36'
                                value={
                                  pendingBulkSupplyCost ??
                                  (visitsData?.bulk_supply_cost != null
                                    ? String(visitsData.bulk_supply_cost)
                                    : '')
                                }
                                onChange={e => {
                                  const raw = e.target.value;
                                  if (isValidDollarInput(raw)) {
                                    setPendingBulkSupplyCost(raw);
                                  }
                                }}
                              />
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </CardContent>
              )}
            </Card>
          </div>
        ) : null}
      </div>

      <div className='flex items-center justify-end gap-2 px-6 py-4 border-t'>
        {saveError && (
          <span className='text-sm text-red-600 mr-auto'>{saveError}</span>
        )}
        <Button variant='outline' onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant='purple' onClick={handleSave} disabled={isSaving}>
          {isSaving ? (
            <>
              <Loader2 className='h-4 w-4 animate-spin mr-2' />
              Saving...
            </>
          ) : (
            'Save'
          )}
        </Button>
      </div>
    </div>
  );
};

export default EOEClaimsWorksheet;
